import type { Chef, Recipe } from '../types';
import {
    extractDomain,
    extractInstagramUsername,
    findChefByFacebookPageSlug,
    findChefByInstagramUsername,
    findChefByUrls,
    getActiveChefs,
    isInstagramDomain,
    normalizeInstagramUsername,
} from './chefDisplay';
import { extractFacebookPageSlug, isFacebookDomain } from './facebookDisplay';

function recipeHasLinks(recipe: Pick<Recipe, 'links'>): boolean {
    return (recipe.links ?? []).some((link) => link.trim().length > 0);
}

export function shouldMigrateRecipeChef(
    recipe: Pick<Recipe, 'chefId' | 'isDeleted' | 'links'>,
): boolean {
    if (recipe.isDeleted) return false;
    if (recipe.chefId?.trim()) return false;
    return recipeHasLinks(recipe);
}

function findChefByInstagramUsernameField(chefs: readonly Chef[], username: string): Chef | null {
    const target = normalizeInstagramUsername(username);
    if (!target) return null;

    return getActiveChefs([...chefs]).find(
        (chef) => normalizeInstagramUsername(chef.instagramUsername) === target,
    ) ?? null;
}

/** Tries to match an existing chef from recipe source links (Instagram, Facebook, website). */
export function resolveChefFromRecipeLinks(
    recipe: Pick<Recipe, 'links' | 'ownerId'>,
    chefs: readonly Chef[],
): Chef | null {
    const links = (recipe.links ?? []).map((link) => link.trim()).filter(Boolean);
    if (links.length === 0) return null;

    const ownerId = recipe.ownerId?.trim() || undefined;
    const chefList = [...chefs];

    const byUrls = findChefByUrls(chefList, links, ownerId);
    if (byUrls) return byUrls;

    for (const link of links) {
        const domain = extractDomain(link);

        if (isInstagramDomain(domain)) {
            const username = extractInstagramUsername(link);
            if (username) {
                const byInstagram = findChefByInstagramUsername(chefList, username, ownerId)
                    ?? findChefByInstagramUsernameField(chefs, username);
                if (byInstagram) return byInstagram;
            }
        }

        if (isFacebookDomain(domain)) {
            const pageSlug = extractFacebookPageSlug(link);
            if (pageSlug) {
                const byFacebook = findChefByFacebookPageSlug(chefList, pageSlug, ownerId);
                if (byFacebook) return byFacebook;
            }
        }
    }

    return null;
}

export interface RecipeChefMigrationCandidate {
    recipe: Recipe;
    chefId: string;
}

export function collectRecipeChefMigrationCandidates(
    recipes: readonly Recipe[],
    chefs: readonly Chef[],
): RecipeChefMigrationCandidate[] {
    const candidates: RecipeChefMigrationCandidate[] = [];

    for (const recipe of recipes) {
        if (!shouldMigrateRecipeChef(recipe)) continue;

        const chef = resolveChefFromRecipeLinks(recipe, chefs);
        if (chef) {
            candidates.push({ recipe, chefId: chef.id });
        }
    }

    return candidates;
}

export function buildRecipeChefFromLinksPatch(chefId: string, now = new Date().toISOString()) {
    return {
        chefId,
        updatedAt: now,
    };
}
